import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Driver, driver } from "driver.js";

import "driver.js/dist/driver.css";
import { TourGlobalStyles } from "./styles";
import {
  analyticsTourConfig,
  auditsTourConfig,
  benchmarksTourConfig,
  dataPageFilesTourConfig,
  dataPageOverviewTourConfig,
  dataPageReviewTourConfig,
  footprintTourConfig,
  homePageTourConfig,
  importAgentTourConfig,
  peerBenchmarksTourConfig,
} from "./tours";
import { TourId, useTourContext } from "@/context/TourContext";
import { trackEvent } from "@/utils/analytics/mixpanel";

const tourConfigs: Partial<
  Record<TourId, { id: TourId; steps: (t: (key: string) => string) => any[] }>
> = {
  homepage: homePageTourConfig,
  audits: auditsTourConfig,
  footprint: footprintTourConfig,
  benchmarks: benchmarksTourConfig,
  analytics: analyticsTourConfig,
  peerBenchmarks: peerBenchmarksTourConfig,
  importAgent: importAgentTourConfig,
  dataPageOverview: dataPageOverviewTourConfig,
  dataPageFiles: dataPageFilesTourConfig,
  dataPageReview: dataPageReviewTourConfig,
};

const ProductTour: React.FC = () => {
  const { t } = useTranslation();
  const { activeTour, endTour, markTourComplete } = useTourContext();
  const driverRef = useRef<Driver | null>(null);

  useEffect(() => {
    if (!activeTour) {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
      return undefined;
    }

    const tourConfig = tourConfigs[activeTour];
    if (!tourConfig) {
      // End tour when no config is found to avoid stuck state
      markTourComplete(activeTour);
      endTour();
      return undefined;
    }

    const totalSteps = tourConfig.steps(t).length;

    // Track tour started
    trackEvent({
      name: "Product Tour Started",
      properties: {
        tourId: activeTour,
        totalSteps,
      },
    });

    driverRef.current = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      steps: tourConfig.steps(t),
      nextBtnText: t("tour.buttons.next"),
      prevBtnText: t("tour.buttons.previous"),
      doneBtnText: t("tour.buttons.done"),
      progressText: `{{current}} / {{total}}`,
      onDestroyStarted: (_element, _step, opts) => {
        const { state } = opts;
        const isCompleted = state.activeIndex === totalSteps - 1;
        trackEvent({
          name: isCompleted ? "Product Tour Completed" : "Product Tour Skipped",
          properties: {
            tourId: activeTour,
            stepsCompleted: (state.activeIndex ?? 0) + 1,
            totalSteps,
          },
        });
        markTourComplete(activeTour);
        endTour();
      },
      onDestroyed: () => {
        markTourComplete(activeTour);
        endTour();
      },
    });

    const timeoutId = setTimeout(() => {
      driverRef.current?.drive();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, [activeTour, t, endTour, markTourComplete]);

  return <TourGlobalStyles />;
};

export default ProductTour;
